import * as React from "react"
import { connect } from "react-redux"
import {
    Body,
    Cell,
    CompleteFooter,
    Header,
    HeaderRow,
    Table,
} from "@tenforce/toolbox-table-react"
import { FieldItems } from "@tenforce/toolbox-field-react"
import { Modal } from "@tenforce/toolbox-modal-react"
import { Action, ActionType } from "@tenforce/toolbox-button-push-react"
import { Symbols } from "@tenforce/toolbox-fontmap"

import bem from "../../../bem"
import Contract from "./Contract"
import Editor from "./Editor"
import Report from "./Report"
import { GlobalState } from "../../../store"
import { DispatchProps, mapDispatchToProps } from "../actions"
import { getSortedContracts, SortedContracts } from "../selectors"
import { Labels } from "../../../util/Translations"

import "./Contracts.scss"
import { IContracts } from "../index"

const b = bem.with("NewUI__contracts")

type StateProps = IContracts

type Props = StateProps & DispatchProps

export class Contracts extends React.PureComponent<Props> {
    render(): JSX.Element {
        const { contracts, settings, editor, report, validating } = this.props
        const {
            TurnOnContractValidation,
            TurnOffContractValidation,
            ValidateAllContracts,
            Contracts,
            Details,
            Name,
            Validation,
        } = Labels
        return (
            <>
                {settings && (
                    <FieldItems extraClasses={b("header")}>
                        {settings?.validationEnabled ? (
                            <Action
                                actionType={ActionType.icon}
                                data-ft-contracts="turn-off-validation"
                                onClick={(): void => this.configure(false)}
                            >
                                {Symbols.ArrowReset}&nbsp;
                                {TurnOffContractValidation}
                            </Action>
                        ) : (
                            <Action
                                actionType={ActionType.icon}
                                data-ft-contracts="turn-on-validation"
                                onClick={(): void => this.configure(true)}
                            >
                                {Symbols.ArrowCheck}&nbsp;
                                {TurnOnContractValidation}
                            </Action>
                        )}
                        <Action
                            actionType={ActionType.icon}
                            data-ft-contracts="validate-all-contracts"
                            isLoading={validating}
                            onClick={this.validateAll}
                        >
                            {Symbols.Todos}&nbsp;
                            {ValidateAllContracts}
                        </Action>
                    </FieldItems>
                )}
                <Table>
                    <Header>
                        <HeaderRow isCondensed={true}>
                            <Cell size="0" extraClasses={b("table__status")} />
                            <Cell size="1">
                                <FieldItems label={Name} />
                            </Cell>
                            <Cell size="0.5">
                                <FieldItems label={Validation} />
                            </Cell>
                            <Cell size="2" />
                        </HeaderRow>
                    </Header>
                    <Body>
                        {contracts.map((contract) => (
                            <Contract
                                key={contract.id}
                                contract={contract}
                                validating={validating}
                                setEnabled={this.setEnabled}
                                show={this.show}
                                validate={this.validate}
                            />
                        ))}
                    </Body>
                    <CompleteFooter>
                        <div className="label is-dimmed">
                            {contracts.length}&nbsp;{Contracts}
                        </div>
                    </CompleteFooter>
                </Table>

                <Modal
                    isShown={editor.visible}
                    containerProps={{
                        isCenterPositioned: true,
                        onClose: this.closeEditor,
                    }}
                    contentProps={{ extraClasses: "tw-my-1/20 tw-mx-1/10" }}
                    key="editor"
                >
                    <Editor
                        title={editor.contract?.name || Details}
                        source={editor.contract?.content}
                    />
                </Modal>
                <Modal
                    isShown={report.visible}
                    containerProps={{
                        isCenterPositioned: true,
                        onClose: this.closeReport,
                    }}
                    contentProps={{ extraClasses: "tw-my-1/20 tw-mx-1/5" }}
                    key="report"
                >
                    <Report />
                </Modal>
            </>
        )
    }

    configure = (validationEnabled: boolean): void => {
        const { updateGlobalSettings } = this.props
        const settings = { validationEnabled }
        updateGlobalSettings(settings)
    }

    show = (id: string): void => {
        const { requestContract } = this.props
        requestContract(id)
    }

    setEnabled = (id: string, enabled: boolean): void => {
        const { enableContract } = this.props
        enableContract(id, enabled)
    }

    validate = (id: string): void => {
        const { validateContract } = this.props
        validateContract(id)
    }

    validateAll = (): void => {
        const { requestContracts } = this.props
        requestContracts(true)
    }

    closeEditor = (): void => {
        const { closeEditor } = this.props
        closeEditor()
    }

    closeReport = (): void => {
        const { closeReport } = this.props
        closeReport()
    }
}

const mapStateToProps = (state: GlobalState): IContracts & SortedContracts => ({
    ...state.contracts,
    sortedContracts: getSortedContracts(state.contracts),
})

export default connect(mapStateToProps, mapDispatchToProps)(Contracts)
